import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { signupOrLogin } from "../../../../auth/api";
import {
  checkPassword,
  checkUsername,
  loadCommonPasswords,
  PASSWORD_MAX_CHARS,
  PASSWORD_MIN_CHARS,
  USERNAME_MAX_CHARS,
  USERNAME_MIN_CHARS,
} from "../../../../auth/password_policy";
import { Icon } from "../../../../components/icons/Icon";
import { normaliseLocale, setLocale } from "../../../../i18n";
import { useAuthStore } from "../../../../auth/auth_store";
import { lightTap, mediumTap } from "../../../../lib/input/haptic";
import { EntryLinks } from "../EntryLinks";

/**
 * Login form — submits to `POST /accounts/login`, falling back to
 * `POST /accounts/signup` on 401. On success the auth slice stores
 * the 32-byte signing key (returned by the server) in memory —
 * survives the session, wiped on logout / page unload.
 *
 * Promoted to a skin slot so forks can swap
 * the chrome without touching the wiring layer. Prop-less (the
 * slot reads `serverBase` + `login` action from the auth store
 * directly, matching the pre-promotion component shape — no
 * external state to push in).
 *
 * **Mobile baseline carries**:
 * 44 pt touch floor on inputs + button; password reveal toggle
 * (stroke eye / eye-off SVG); `autoCapitalize="none"
 * autoCorrect="off" spellCheck=false`
 * on both fields so iOS Safari doesn't auto-capitalize the
 * case-sensitive username; safe-area-inset padding on the shell;
 * `aria-busy` + `aria-live="assertive"` error region; autoFocus
 * on the username field; haptic mediumTap on submit. Signup
 * mode gates a min-length password (≥ 8 chars) client-side; the
 * inline strength meter + recovery flow defer to
 * a future password-strength / recovery iteration.
 *
 * **Explicit signup / login tab pattern**: the form surfaces two
 * tabs ("로그인" / "회원가입") modelled after the Lobby
 * host/guest tabpanel pattern. The active mode drives the submit
 * button label + the password input's `autoComplete`
 * (`"current-password"` for login,
 * `"new-password"` for signup so password managers offer a
 * generated value).
 *
 * **Signup states its rules up front.** Each rule the server applies
 * (`auth/password_policy.ts`) is listed under its field and ticks as
 * it is met, so nobody learns them one refusal at a time; the submit
 * stays disabled until they all hold. A second password field and a
 * warning cover what a typo would cost: a lost password cannot be
 * recovered — the signing key is sealed under it. Login mode shows
 * none of this (existing accounts may predate the rules).
 */

type AuthMode = "login" | "signup";

type RuleState = "idle" | "ok" | "bad";

function ruleState(touched: boolean, ok: boolean | null): RuleState {
  if (!touched || ok === null) return "idle";
  return ok ? "ok" : "bad";
}

function Rule({
  state,
  children,
}: {
  state: RuleState;
  children: ReactNode;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <li className={`login-form__rule login-form__rule--${state}`}>
      <Icon name={state === "bad" ? "close" : "check"} size={14} />
      <span>{children}</span>
      {state === "idle" ? null : (
        <span className="sr-only">
          {state === "ok" ? t("auth.rule_met") : t("auth.rule_unmet")}
        </span>
      )}
    </li>
  );
}

function LoginImpl(): JSX.Element {
  const { t } = useTranslation();
  const login = useAuthStore((s) => s.login);
  const serverBase = useAuthStore((s) => s.serverBase);
  const [mode, setMode] = useState<AuthMode>("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [commonPasswords, setCommonPasswords] =
    useState<ReadonlySet<string> | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const usernameRef = useRef<HTMLInputElement>(null);

  // Tab switch focus management mirror of the Lobby tab
  // pattern. On every mode change, pull focus to the username
  // input after React's commit. Skipped on the initial render
  // via `hasMountedRef` so the page doesn't yank focus on mount
  // (autoFocus already lands there once).
  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    const t = window.setTimeout(() => usernameRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [mode]);

  const signup = mode === "signup";
  useEffect(() => {
    if (!signup || commonPasswords !== null) return;
    let live = true;
    loadCommonPasswords()
      .then((set) => {
        if (live) setCommonPasswords(set);
      })
      // Unknown until it loads; the server refuses a common password
      // either way.
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [signup, commonPasswords]);
  const usernameChecks = checkUsername(username.trim());
  const passwordChecks = checkPassword(username, password, commonPasswords);
  const confirmMatches = passwordConfirm === password;
  const signupReady =
    usernameChecks.length &&
    usernameChecks.charset &&
    passwordChecks.minLength &&
    passwordChecks.maxLength &&
    passwordChecks.notUsername &&
    passwordChecks.notCommon !== false &&
    passwordConfirm !== "" &&
    confirmMatches;

  const onSelectLogin = useCallback((): void => {
    lightTap();
    setMode("login");
    setError(null);
  }, []);
  const onSelectSignup = useCallback((): void => {
    lightTap();
    setMode("signup");
    setError(null);
  }, []);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (username.trim() === "" || password === "") {
      setError(t("auth.missing_credentials"));
      return;
    }
    if (signup && !signupReady) return;
    mediumTap();
    setSubmitting(true);
    try {
      // The active tab is the user's EXPLICIT intent and gates the
      // wire path. In login mode a 401 (wrong password) surfaces the
      // real "login failed" message — it never silently falls back to
      // signup (which would 409 as a misleading "username taken").
      // Only signup mode keeps the register affordance: a fresh
      // username creates the account, an existing one with the same
      // password logs straight in, and a password mismatch surfaces
      // the friendly 409 conflict.
      const result = await signupOrLogin({
        serverBase,
        username: username.trim(),
        password,
        mode,
      });
      login({
        username: result.username,
        accountId: result.accountId,
        playerIdHex: result.playerIdHex,
        signingKeyHex: result.signingKeyHex,
        chipFree: result.chipFree,
        chipInPlay: result.chipInPlay,
        chipHoldTotal: result.chipHoldTotal,
        // Forward the account locale into the i18n negotiation
        // chain. `null` when the row never set one; the chain
        // falls through to localStorage / navigator.
        locale: result.locale,
        // CSRF Double-Submit token (ADR-010). `authedRequest` reads
        // this slot for every mutating fetch.
        csrfToken: result.csrfToken,
        mlDsa: result.mlDsa,
      });
      // If the account row carries a locale
      // preference, surface it via `setLocale` so the active
      // i18n bundle swaps immediately (without waiting for a
      // page reload). No-op when `result.locale` is `null`.
      if (result.locale !== null) {
        // The negotiation helper canonicalises the BCP-47 tag before
        // applying.
        const normalised = normaliseLocale(result.locale);
        if (normalised !== null) await setLocale(normalised);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  // Dynamic autoComplete. `new-password` triggers iOS
  // / Android / desktop password managers to OFFER a generated
  // value at signup time; `current-password` triggers AUTOFILL
  // at login. Wiring off the active tab so a single input
  // serves both flows without an explicit prop.
  const passwordAutoComplete =
    mode === "signup" ? "new-password" : "current-password";

  return (
    <main
      id="main-content"
      className="login-shell"
      aria-label={t("auth.form_aria")}
    >
      <form className="login-form" onSubmit={onSubmit} aria-busy={submitting}>
        <h1 className="login-form__title">{t("auth.title")}</h1>
        <p className="login-form__subtitle">{t("auth.subtitle")}</p>
        {/* Signup / login tab pattern (Lobby tab mirror). Both
         *  tabs surface as `role="tab"` + share
         *  the same panel root (single form) — the panel is
         *  `aria-labelledby` 두 tab 동시 cite via the active
         *  tab's id. */}
        <div
          className="login-form__tabs"
          role="tablist"
          aria-label={t("auth.tabs_aria")}
        >
          <button
            type="button"
            role="tab"
            id="login-tab-login"
            aria-controls="login-panel"
            aria-selected={mode === "login"}
            tabIndex={mode === "login" ? 0 : -1}
            className={
              mode === "login"
                ? "login-form__tab login-form__tab--active"
                : "login-form__tab"
            }
            onClick={onSelectLogin}
          >
            {t("auth.tab_login")}
          </button>
          <button
            type="button"
            role="tab"
            id="login-tab-signup"
            aria-controls="login-panel"
            aria-selected={mode === "signup"}
            tabIndex={mode === "signup" ? 0 : -1}
            className={
              mode === "signup"
                ? "login-form__tab login-form__tab--active"
                : "login-form__tab"
            }
            onClick={onSelectSignup}
          >
            {t("auth.tab_signup")}
          </button>
        </div>
        <div
          role="tabpanel"
          id="login-panel"
          aria-labelledby={
            mode === "signup" ? "login-tab-signup" : "login-tab-login"
          }
          className="login-form__panel"
        >
          <label htmlFor="username">{t("auth.username_label")}</label>
          <input
            ref={usernameRef}
            id="username"
            type="text"
            inputMode="text"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoFocus
            enterKeyHint="next"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={submitting}
            aria-describedby={signup ? "signup-username-rules" : undefined}
          />
          {signup ? (
            <ul id="signup-username-rules" className="login-form__rules">
              <Rule
                state={ruleState(
                  username.trim() !== "",
                  usernameChecks.charset,
                )}
              >
                {t("auth.rule_username_charset")}
              </Rule>
              <Rule
                state={ruleState(username.trim() !== "", usernameChecks.length)}
              >
                {t("auth.rule_username_length", {
                  min: USERNAME_MIN_CHARS,
                  max: USERNAME_MAX_CHARS,
                })}
              </Rule>
            </ul>
          ) : null}
          <label htmlFor="password">{t("auth.password_label")}</label>
          <div className="login-form__password-group">
            <input
              id="password"
              type={passwordVisible ? "text" : "password"}
              autoComplete={passwordAutoComplete}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint={signup ? "next" : "go"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
              aria-describedby={signup ? "signup-password-rules" : undefined}
            />
            <button
              type="button"
              className="login-form__password-toggle"
              onClick={() => setPasswordVisible((v) => !v)}
              disabled={submitting}
              aria-label={
                passwordVisible
                  ? t("auth.password_hide")
                  : t("auth.password_show")
              }
              aria-pressed={passwordVisible}
            >
              {passwordVisible ? (
                /* Eye-off — outlined open-source icon
                 * (Lucide-style stroke). Replaces the
                 * monkey-see-no-evil emoji per user cite
                 * 2026-05-24 — the standalone glyph reads as
                 * "password hidden" without the cultural
                 * baggage of the emoji surface. */
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-10-8-10-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="2" y1="2" x2="22" y2="22" />
                </svg>
              ) : (
                /* Eye — outlined open-source icon. Replaces
                 * the 👁 surface the user cite called "섬찟"
                 * (eerie). The plain stroke icon reads as
                 * "password visible toggle" at a glance. */
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
          {signup ? (
            <>
              <ul id="signup-password-rules" className="login-form__rules">
                <Rule
                  state={ruleState(
                    password !== "",
                    passwordChecks.minLength && passwordChecks.maxLength,
                  )}
                >
                  {passwordChecks.maxLength
                    ? t("auth.rule_password_min", { min: PASSWORD_MIN_CHARS })
                    : t("auth.rule_password_max", { max: PASSWORD_MAX_CHARS })}
                </Rule>
                <Rule
                  state={ruleState(password !== "", passwordChecks.notUsername)}
                >
                  {t("auth.rule_password_not_username")}
                </Rule>
                <Rule
                  state={ruleState(password !== "", passwordChecks.notCommon)}
                >
                  {t("auth.rule_password_not_common")}
                </Rule>
              </ul>
              <label htmlFor="password-confirm">
                {t("auth.password_confirm_label")}
              </label>
              <input
                id="password-confirm"
                type={passwordVisible ? "text" : "password"}
                autoComplete="new-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="go"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                disabled={submitting}
                aria-describedby="signup-confirm-rule"
              />
              <ul id="signup-confirm-rule" className="login-form__rules">
                <Rule state={ruleState(passwordConfirm !== "", confirmMatches)}>
                  {t("auth.rule_password_confirm")}
                </Rule>
              </ul>
              <p className="login-form__warning">
                <Icon name="alert-triangle" size={16} />
                <span>{t("auth.password_loss_warning")}</span>
              </p>
            </>
          ) : null}
          {error !== null ? (
            <div
              role="alert"
              aria-live="assertive"
              aria-atomic="true"
              className="login-form__error"
            >
              {error}
            </div>
          ) : null}
          <button
            type="submit"
            className="login-form__submit"
            disabled={submitting || (signup && !signupReady)}
            aria-busy={submitting}
          >
            {submitting
              ? mode === "signup"
                ? t("auth.submit_busy_signup")
                : t("auth.submit_busy_login")
              : mode === "signup"
                ? t("auth.submit_signup")
                : t("auth.submit_login")}
          </button>
        </div>
        <EntryLinks />
      </form>
    </main>
  );
}

export const Login = memo(LoginImpl);
